import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import ContactSelect from '../components/ui/ContactSelect';
import SafeIcon from '../common/SafeIcon';
import { formatSerial, formatDate, todayLocal } from '../lib/utils';
import { FiUpload, FiSearch } from 'react-icons/fi';

export default function Disposition() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchSerial, setSearchSerial] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFirearm, setSelectedFirearm] = useState(null);
  const [formData, setFormData] = useState({
    disposed_to_id: '',
    disposition_date: todayLocal(),
    form_4473_reference: ''
  });

  const handleSearch = async () => {
    const all = await api.getBoundBook(tenant.id);
    const term = searchSerial.toLowerCase().trim();
    const found = all.filter(
      i => i.status === 'IN_HOUSE' && (
        (i.serial_number?.toLowerCase() || '').includes(term) ||
        (i.manufacturer?.toLowerCase() || '').includes(term) ||
        (i.model?.toLowerCase() || '').includes(term)
      )
    );
    if (found.length === 1) {
      setSelectedFirearm(found[0]);
      setSearchResults([]);
    } else if (found.length > 1) {
      setSearchResults(found);
    } else {
      setSearchResults([]);
      alert('No matching in-house firearms found.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFirearm) return;
    setLoading(true);
    try {
      await api.disposeFirearm(tenant.id, selectedFirearm.id, formData);
      navigate('/bound-book');
    } catch (err) {
      console.error('Disposition error:', err);
      alert(err.message || 'Error recording disposition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
        Dispose Firearm
      </h2>

      {!selectedFirearm ? (
        <Card>
          <CardHeader>
            <CardTitle>Find Firearm</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by Serial, Manufacturer, or Model"
                value={searchSerial}
                onChange={(e) => setSearchSerial(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} icon={FiSearch}>Search</Button>
            </div>
            {searchResults.length > 0 && (
              <div className="border rounded-md divide-y dark:border-slate-700 max-h-64 overflow-y-auto">
                <p className="px-4 py-2 text-xs text-slate-500 bg-slate-50 dark:bg-slate-800">
                  {searchResults.length} matches found — select one:
                </p>
                {searchResults.map(item => (
                  <button
                    key={item.id}
                    onClick={() => { setSelectedFirearm(item); setSearchResults([]); }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <span className="font-mono font-medium">
                      {formatSerial(item.serial_number, item.is_non_serialized)}
                    </span>
                    {' — '}
                    <span>{item.manufacturer} {item.model}</span>
                    <span className="text-sm text-slate-500 ml-2">({item.caliber})</span>
                    <span className="text-xs text-slate-400 ml-2">
                      Acq: {formatDate(item.acquisition_date)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Disposition Details</CardTitle>
            <p className="text-sm text-slate-500">
              Disposing: {selectedFirearm.manufacturer} {selectedFirearm.model} ({formatSerial(selectedFirearm.serial_number, selectedFirearm.is_non_serialized)})
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <ContactSelect
                label="Disposed To"
                value={formData.disposed_to_id}
                onChange={(val) => setFormData({...formData, disposed_to_id: val})}
                required
                placeholder="Select buyer / recipient"
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Disposition Date"
                  type="date"
                  name="disposition_date"
                  value={formData.disposition_date}
                  onChange={(e) => setFormData({...formData, disposition_date: e.target.value})}
                  required
                />
                <Input
                  label="4473 Reference (Optional)"
                  name="form_4473_reference"
                  value={formData.form_4473_reference}
                  onChange={(e) => setFormData({...formData, form_4473_reference: e.target.value})}
                />
              </div>

              <div className="flex gap-3 pt-4">
                 <Button type="button" variant="secondary" onClick={() => setSelectedFirearm(null)} className="w-1/3">
                  Cancel
                </Button>
                <Button type="submit" className="w-2/3" isLoading={loading} icon={FiUpload} disabled={!formData.disposed_to_id}>
                  Complete Disposition
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
